import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Swiper, { Navigation as SwiperNavigation, Scrollbar } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';

import Product from './Products/Product';
import ProductVariant from './Products/ProductVariant';
import ProductFilters from './ProductFilters';
import LoadMore from './Products/LoadMore';

/**
 * Product browser component
 *
 * Shows the products available within a section
 *
 * @author John Noel <john.noel@joipolloi.com>
 * @package Minifigs
 */
const Products = ({
    products,
    section,
    selectedProducts,
    onSelectNamedProduct,
    selected,
    onSelect,
    onRemove,
    onCustomise,
    projectTitle,
    skintone,
    currentBuildIndex,
}) => {
    const [isTablet, setisTablet] = useState(window.matchMedia('(min-width: 0px) and (max-width: 980px)'));
    const [sliderInitialised, setSliderInitialised] = useState(false);
    const [swiperState, setSwiperState] = useState(null);
    const sliderRef = useRef(null);
    const scrollContainer = useRef(null);

    const destroySlider = useCallback(() => {
        swiperState.destroy();
        setSliderInitialised(false);
    }, [swiperState]);

    useEffect(() => {
        if (!sliderRef.current) return;

        initSlider(sliderRef.current);
    }, [sliderRef]);

    useEffect(() => {
        const handleResize = () => {
            setisTablet(window.matchMedia('(min-width: 0px) and (max-width: 980px)'));
        };

        window.addEventListener('load', handleResize);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (isTablet.matches && !sliderInitialised && sliderRef.current) {
            initSlider(sliderRef.current);
        }

        if (!isTablet.matches && sliderInitialised) {
            destroySlider();
        }
    }, [destroySlider, isTablet, sliderInitialised]);

    const initSlider = (slider) => {
        const swiper = new Swiper(slider, {
            // configure Swiper to use modules
            modules: [SwiperNavigation, Scrollbar],
            spaceBetween: 20,
            slidesPerView: 2.75,
            loop: false,
            breakpoints: {
                480: {
                    slidesPerView: 3.5,
                },
                768: {
                    slidesPerView: 4.5,
                },
                1024: {
                    slidesPerView: 5.5,
                },
                1500: {
                    slidesPerView: 7.5,
                },
            },
            scrollbar: {
                el: sliderRef.current.querySelector('.swiper-scrollbar'),
                draggable: true,
                hide: false,
            },
            observer: true,
        });

        setSwiperState(swiper);
        setSliderInitialised(true);
    };

    const splitPairs = (arr) => {
        var pairs = [];
        for (let i = 0; i < arr.length; i += 3) {
            if (arr[i + 2] !== undefined) {
                pairs.push([arr[i], arr[i + 1], arr[i + 2]]);
            } else if (arr[i + 1] !== undefined) {
                pairs.push([arr[i], arr[i + 1]]);
            } else {
                pairs.push([arr[i]]);
            }
        }
        return pairs;
    };

    const productPairsArray = splitPairs(products);

    return (
        <div className={`products-container ${isTablet.matches ? 'tablet' : 'desktop'} `} ref={sliderRef}>
            <ProductFilters />

            <ul ref={scrollContainer} className="products products-3 swiper-wrapper">
                {productPairsArray.map((productPair) => {
                    return (
                        <div className="swiper-slide" key={productPair[0].node.id}>
                            {section !== 'heads' &&
                                productPair.map((product) => {
                                    return (
                                        <Product
                                            key={product.node.id}
                                            product={product.node}
                                            section={section}
                                            selectedProducts={selectedProducts}
                                            isSelected={selected !== null && product.node.id == selected.product.id}
                                            onSelect={(
                                                product,
                                                attributes,
                                                addons,
                                                product_attribute,
                                                productFormJsonValues
                                            ) =>
                                                onSelect(
                                                    product,
                                                    attributes,
                                                    addons,
                                                    product_attribute,
                                                    productFormJsonValues
                                                )
                                            }
                                            onSelectNamedProduct={(
                                                currentBuildIndex,
                                                product,
                                                attributes,
                                                addons,
                                                product_attribute,
                                                productSection,
                                                productFormJsonValues
                                            ) =>
                                                onSelectNamedProduct(
                                                    currentBuildIndex,
                                                    product,
                                                    attributes,
                                                    addons,
                                                    product_attribute,
                                                    productSection,
                                                    productFormJsonValues
                                                )
                                            }
                                            projectTitle={projectTitle}
                                            onRemove={(section) => onRemove(section)}
                                            onCustomise={(product) => onCustomise(product)}
                                            boldProductOptions={product.boldProductOptions}
                                            currentBuildIndex={currentBuildIndex}
                                        />
                                    );
                                })}
                            {section === 'heads' &&
                                productPair.map((product) => {
                                    {
                                        return product?.node?.variants?.edges?.map((variant) => {
                                            if (!variant.node.skintoneValue) {
                                                console.error(`No skintone set for product node: ${variant.node.id}`);
                                            }
                                            if (
                                                variant.node.skintoneValue?.value.toLowerCase() ===
                                                skintone.toLowerCase()
                                            ) {
                                                return (
                                                    <ProductVariant
                                                        key={variant.node.id}
                                                        parentProduct={product.node}
                                                        product={variant.node}
                                                        section={section}
                                                        productTitle={product.node.title}
                                                        isSelected={
                                                            selected !== null &&
                                                            variant.node.id == selected.productMatchAttr?.id
                                                        }
                                                        onSelect={(
                                                            variant,
                                                            attributes,
                                                            addons,
                                                            product_attribute,
                                                            productFormJsonValues
                                                        ) =>
                                                            onSelect(
                                                                variant,
                                                                attributes,
                                                                addons,
                                                                product_attribute,
                                                                productFormJsonValues
                                                            )
                                                        }
                                                        projectTitle={projectTitle}
                                                        boldProductOptions={product.boldProductOptions}
                                                        onRemove={(section) => onRemove(section)}
                                                        onCustomise={(product) => onCustomise(product)}
                                                    />
                                                );
                                            }
                                        });
                                    }
                                })}
                        </div>
                    );
                })}

                <div className="load-more-slide swiper-slide">
                    <LoadMore swiperState={swiperState} scrollContainer={scrollContainer} />
                </div>
            </ul>

            <div className="swiper-scrollbar"></div>
        </div>
    );
};

Products.propTypes = {
    products: PropTypes.array.isRequired,
    selected: PropTypes.object,
    onSelect: PropTypes.func,
    onRemove: PropTypes.func,
    onCustomise: PropTypes.func,
};

Products.defaultProps = {
    selected: null,
    onSelect: () => {},
    onRemove: () => {},
    onCustomise: () => {},
};

export default Products;
